import React, { useEffect, useState } from "react";
import { adminApi, publicApi } from "../../lib/api";
import { toast } from "../../hooks/use-toast";
import { Save, Power, Mail, Send, Plus, X, ShieldCheck } from "lucide-react";

const CMSSettings = () => {
  const [prelaunch, setPrelaunch] = useState(false);
  const [applicationsMode, setApplicationsMode] = useState("waitlist");
  const [allowlist, setAllowlist] = useState([]);
  const [newAllow, setNewAllow] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingAllow, setSavingAllow] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    adminApi.get("/admin/settings")
      .then(({ data }) => {
        setPrelaunch(!!data.prelaunch_mode);
        setApplicationsMode(data.applications_mode || "waitlist");
        setAllowlist(data.test_email_allowlist || []);
      })
      .catch(() => publicApi.get("/settings").then(({ data }) => { setPrelaunch(!!data.prelaunch_mode); setApplicationsMode(data.applications_mode || "waitlist"); }))
      .catch(() => toast({ title: "Failed to load settings" }))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await adminApi.patch("/admin/settings", { prelaunch_mode: prelaunch, applications_mode: applicationsMode });
      setPrelaunch(!!data.prelaunch_mode);
      setApplicationsMode(data.applications_mode || "waitlist");
      toast({ title: "Settings saved" });
    } catch (err) {
      toast({ title: "Save failed", description: err?.response?.data?.detail || "Try again." });
    } finally { setSaving(false); }
  };

  const saveAllowlist = async (next) => {
    setSavingAllow(true);
    try {
      const { data } = await adminApi.patch("/admin/settings", { test_email_allowlist: next });
      setAllowlist(data.test_email_allowlist || []);
      toast({ title: "Test-email allowlist updated" });
    } catch (err) {
      toast({ title: "Update failed", description: err?.response?.data?.detail || "Try again" });
    } finally { setSavingAllow(false); }
  };

  const addAllow = (e) => {
    e.preventDefault();
    const v = newAllow.trim().toLowerCase();
    if (!v || !/^\S+@\S+\.\S+$/.test(v)) { toast({ title: "Enter a valid email address" }); return; }
    if (allowlist.includes(v)) { toast({ title: "Already on the allowlist" }); return; }
    const next = [...allowlist, v].sort();
    saveAllowlist(next);
    setNewAllow("");
  };
  const removeAllow = (email) => {
    const next = allowlist.filter(e => e !== email);
    saveAllowlist(next);
  };

  const sendTest = async (e) => {
    e.preventDefault();
    if (!testEmail) return;
    setSendingTest(true); setLastResult(null);
    try {
      const { data } = await adminApi.post("/admin/test-email", { to: testEmail });
      setLastResult(data);
      if (data.relayed) {
        toast({ title: "Sent via relay", description: `Delivered to ${data.delivered_to} instead of ${data.intended_to}. Remove EMAIL_RELAY_TO to send directly.` });
      } else {
        toast({ title: "Test email sent", description: `Delivered directly to ${data.delivered_to}.` });
      }
    } catch (err) {
      toast({ title: "Send failed", description: err?.response?.data?.detail || "Check backend logs and Resend dashboard." });
    } finally { setSendingTest(false); }
  };

  if (loading) return <div className="p-10 text-center text-[14px] text-[#0d2a20]/60">Loading settings…</div>;

  return (
    <div className="rounded-lg p-8" style={{ background: '#fbf8ef' }}>
      <div className="max-w-2xl">
        <div className="section-eyebrow text-[#0d2a20]/60 mb-2">Global settings</div>
        <h2 className="hero-title text-[28px] leading-tight mb-6">Site controls</h2>

        <div className="rounded-md bg-white border p-5 mb-4" style={{ borderColor: 'rgba(13,42,32,0.08)' }} data-testid="settings-prelaunch-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Power size={16} className={prelaunch ? "text-[#e0684d]" : "text-[#0d2a20]/40"} />
                <div className="text-[15px] font-medium">Pre-launch mode</div>
              </div>
              <p className="text-[13px] text-[#0d2a20]/70 leading-relaxed">
                When ON, all public visitors see only the waitlist landing page. Members can still sign in and admins can still access the dashboard. This setting is <b>independent</b> of how new applications are handled — see below.
              </p>
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer shrink-0" data-testid="settings-prelaunch-toggle">
              <input type="checkbox" checked={prelaunch} onChange={e => setPrelaunch(e.target.checked)} className="h-5 w-5 accent-[#e0684d]" />
              <span className="text-[13px] font-medium">{prelaunch ? "ON" : "OFF"}</span>
            </label>
          </div>
        </div>

        <div className="rounded-md bg-white border p-5 mb-4" style={{ borderColor: 'rgba(13,42,32,0.08)' }} data-testid="settings-applications-mode-card">
          <div className="mb-3">
            <div className="text-[15px] font-medium mb-1">New Diaspora Network applications</div>
            <p className="text-[13px] text-[#0d2a20]/70 leading-relaxed">
              Controls what happens to newly submitted applications. <b>Independent</b> from Pre-launch mode — you can have the full site live and still hold new applications on a waitlist. Only affects new submissions.
            </p>
            <div className="mt-1 text-[12px] text-[#0d2a20]/60">Currently active: <b>{applicationsMode === 'waitlist' ? 'Waitlist New Applications' : 'Accept Applications for Review'}</b></div>
          </div>
          <div className="grid gap-2">
            <label className="flex items-start gap-3 p-3 rounded-md cursor-pointer" style={{ background: applicationsMode === 'waitlist' ? '#fde5c7' : 'transparent', border: '1px solid rgba(13,42,32,0.1)' }} data-testid="settings-apps-mode-waitlist">
              <input type="radio" name="apps-mode" value="waitlist" checked={applicationsMode === 'waitlist'} onChange={e => setApplicationsMode(e.target.value)} className="mt-1 accent-[#e0684d]" />
              <div>
                <div className="text-[14px] font-medium">Waitlist New Applications</div>
                <div className="text-[12px] text-[#0d2a20]/70">Every new applicant is marked <b>waitlisted</b> and receives the waitlist confirmation email. No approval workflow starts automatically.</div>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 rounded-md cursor-pointer" style={{ background: applicationsMode === 'review' ? '#d6ecd6' : 'transparent', border: '1px solid rgba(13,42,32,0.1)' }} data-testid="settings-apps-mode-review">
              <input type="radio" name="apps-mode" value="review" checked={applicationsMode === 'review'} onChange={e => setApplicationsMode(e.target.value)} className="mt-1 accent-[#e0684d]" />
              <div>
                <div className="text-[14px] font-medium">Accept Applications for Review</div>
                <div className="text-[12px] text-[#0d2a20]/70">New applicants enter the normal review workflow (<i>new / reviewing / approved / declined</i>) and receive the standard confirmation email.</div>
              </div>
            </label>
          </div>
        </div>

        <button onClick={save} disabled={saving} data-testid="settings-save-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-coral text-[13px] disabled:opacity-60">
          <Save size={14} /> {saving ? "Saving…" : "Save settings"}
        </button>

        <div className="section-eyebrow text-[#0d2a20]/60 mt-12 mb-2">Testing safeguards</div>
        <h2 className="hero-title text-[24px] leading-tight mb-4">Approved test email addresses</h2>
        <div className="rounded-md bg-white border p-5 mb-6" style={{ borderColor: 'rgba(13,42,32,0.08)' }} data-testid="settings-test-allowlist-card">
          <div className="flex items-start gap-2 mb-3">
            <ShieldCheck size={16} className="text-[#154a1c] mt-0.5" />
            <p className="text-[13px] text-[#0d2a20]/70 leading-relaxed">
              <b>Hard safeguard.</b> Only the email addresses on this list can receive test emails from Vemoye — including <i>Send test email</i>, <i>Email template test-send</i>, QA and any future test/preview flow. Real applicant/member emails are never automatically added. All test-send attempts to non-allowed addresses are blocked server-side and logged to the admin audit log.
            </p>
          </div>
          <form onSubmit={addAllow} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mb-3">
            <div className="relative flex-1">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0d2a20]/40" />
              <input type="email" required placeholder="qa+alias@yourdomain.com" value={newAllow} onChange={e => setNewAllow(e.target.value)} data-testid="settings-allowlist-input" className="w-full pl-9 pr-3 py-2 rounded-md bg-white border text-[13px]" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
            </div>
            <button type="submit" disabled={savingAllow} data-testid="settings-allowlist-add-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-forest text-[13px] disabled:opacity-60 shrink-0"><Plus size={14} /> Add</button>
          </form>
          {allowlist.length === 0 ? (
            <div className="text-[12px] text-[#8a4a10] rounded-md p-2" style={{ background: '#fde5c7' }}>No addresses on the allowlist yet — <b>all test-email attempts will be blocked</b> until you add at least one.</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {allowlist.map(e => (
                <span key={e} className="inline-flex items-center gap-1 pl-3 pr-1 py-1 rounded-full text-[12px]" style={{ background: '#d6ecd6', color: '#154a1c' }} data-testid={`settings-allowlist-chip-${e}`}>
                  {e}
                  <button onClick={() => removeAllow(e)} className="ml-1 rounded-full w-5 h-5 inline-flex items-center justify-center hover:bg-[#154a1c]/10" aria-label={`Remove ${e}`}><X size={11} /></button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="section-eyebrow text-[#0d2a20]/60 mt-8 mb-2">Email</div>
        <h2 className="hero-title text-[24px] leading-tight mb-4">Send a test email</h2>
        <div className="rounded-md bg-white border p-5" style={{ borderColor: 'rgba(13,42,32,0.08)' }} data-testid="settings-test-email-card">
          <p className="text-[13px] text-[#0d2a20]/70 leading-relaxed mb-4">
            Sends a small email via Resend. <b>Only addresses on the Approved test email addresses list above will be accepted</b> — attempts to send to real applicants or members are blocked server-side.
          </p>
          <form onSubmit={sendTest} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="relative flex-1">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0d2a20]/40" />
              <input
                type="email"
                required
                placeholder="you@example.com"
                value={testEmail}
                onChange={e => setTestEmail(e.target.value)}
                data-testid="settings-test-email-input"
                className="w-full pl-9 pr-3 py-2 rounded-md bg-white border text-[13px]"
                style={{ borderColor: 'rgba(13,42,32,0.15)' }}
              />
            </div>
            <button type="submit" disabled={sendingTest || !testEmail} data-testid="settings-test-email-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-coral text-[13px] disabled:opacity-60 shrink-0">
              <Send size={14} /> {sendingTest ? "Sending…" : "Send test email"}
            </button>
          </form>
          {lastResult && (
            <div className="mt-4 text-[12px] rounded-md p-3" style={{ background: lastResult.relayed ? '#fde5c7' : '#d6ecd6', color: lastResult.relayed ? '#8a4a10' : '#154a1c' }}>
              {lastResult.relayed
                ? <>Sent via relay to <b>{lastResult.delivered_to}</b> (intended <b>{lastResult.intended_to}</b>). Relay is still active — remove <code>EMAIL_RELAY_TO</code> from backend/.env to send directly.</>
                : <>Delivered directly to <b>{lastResult.delivered_to}</b>. Message id: <code>{lastResult.message_id || '—'}</code>. Applicants will now receive activation emails at their real address.</>
              }
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CMSSettings;
